import * as React from "react";
import { LinksProps } from "./typings";
import { UitkCard, UitkCardTextSegment, UitkCardContent, UitkCardSectionSeparatorType } from "uitk-react-cards";
import { UitkIcon } from "uitk-react-icons";
import { UitkLink } from "uitk-react-link";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { DefaultFlexModuleResult, Link } from "typings/microserviceModels/links-flex-module";
import { LocalizedText } from "bernie-l10n";
import { GeoType } from "src/typings/flexFramework/FlexDefinitions";
import { LocationType } from "src/components/utility/LocationUtils";
import { InsideTabGroupContext } from "components/shared/InsideTabGroupContext/InsideTabGroupContext";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";

const getUitkLink = (model: DefaultFlexModuleResult, el: Link) => {
  const text = `${el.text} ${(el.auxiliaryText && `(${el.auxiliaryText})`) || ""}`;

  if (!el.url) {
    return <span className="uitk-link-layout-default uitk-type-300">{text}</span>;
  }

  return (
    <UitkLink>
      <TrackedLink moduleName={model.name} href={el.url} rfrr={model.strategy} linkName={el.text}>
        {text}
      </TrackedLink>
    </UitkLink>
  );
};

const SeeAllContentLink = ({
  model,
  location,
  locationType
}: {
  model: DefaultFlexModuleResult;
  location: string;
  locationType?: GeoType;
}) => {
  const useNearPreposition =
    locationType &&
    (locationType === LocationType.AIRPORT ||
      locationType === LocationType.POINT_OF_INTEREST ||
      locationType === LocationType.METRO_STATION ||
      locationType === LocationType.TRAIN_STATION);

  const linksMessage = useNearPreposition ? "links.seeAllHotelsNear" : "links.seeAllHotels";

  return (
    <UitkLink>
      <TrackedLink
        moduleName={model.name}
        href={model.seeAllHotelLinks}
        rfrr={model.strategy}
        linkName={model.seeAllHotelLinks}
      >
        <LocalizedText message={linksMessage} data={[location]} />
      </TrackedLink>
    </UitkLink>
  );
};

/**
 * Card with links. Handled three different ways. Either it will display
 * auxiliary content in parenthesis, with an additional card to see more info,
 * or none of the former.
 *
 * Mock flexComponent prop received: @see {.offline/viewModels/Links.json}
 *
 */
export const Links: React.FC<LinksProps> = props => {
  const { flexComponent } = props;
  const model = flexComponent.model;

  if (!flexComponent || !model || !model.links || model.links.length === 0) {
    return null;
  }

  const context = props.context;
  const location = model.locationName || context.searchContext.location?.name;
  const locationType = model.locationType || context.searchContext.location?.type;
  const keyHelper = new ItemKeyHelper("links");

  const [toggle, setToggle] = React.useState(props.flexComponent.model.toggle === "collapsed" ? true : false);

  const flipToggle = (): void => {
    setToggle(!toggle);
  };

  const insideTabGroup = React.useContext(InsideTabGroupContext);
  const paddingClassName = insideTabGroup ? "" : "all-t-padding-three";
  const tabPanelClassName =
    toggle && !insideTabGroup
      ? "all-x-padding-three no-bullet all-y-padding-three"
      : "all-x-padding-four no-bullet all-y-padding-two";

  return (
    <div
      className={`Links uitk-cell all-cell-1-1 ${paddingClassName}`}
      id={flexComponent.id}
      data-fm={flexComponent.fmId}
      data-fm-title-id={flexComponent.fmTitleId}
    >
      <UitkCard>
        <UitkCardContent>
          {!insideTabGroup && (
            <div role="button" aria-expanded={!toggle} className="header" onClick={flipToggle}>
              <UitkCardTextSegment
                className={`uitk-flex uitk-flex-align-items-center all-y-padding-four ${toggle && "allBorderZero"}`}
                separator={UitkCardSectionSeparatorType.BOTTOM}
              >
                <h3 className="uitk-type-heading-500">{model.title}</h3>
                <UitkIcon name={`expand_${toggle ? "more" : "less"}`} />
              </UitkCardTextSegment>
            </div>
          )}
          <div className={`uitk-cell ${toggle && !insideTabGroup ? "is-visually-hidden" : ""}`}>
            <UitkLayoutGrid tag="ul" space="one" className={tabPanelClassName} columns={2}>
              {model.links.map(el => (
                <UitkLayoutGridItem colSpan={1} key={keyHelper.next()}>
                  <li>{getUitkLink(model, el)}</li>
                </UitkLayoutGridItem>
              ))}
            </UitkLayoutGrid>
            {model.seeAllHotelLinks && location && (
              <UitkCardTextSegment separator={UitkCardSectionSeparatorType.TOP} className="seeAllSection">
                <SeeAllContentLink model={model} location={location} locationType={locationType} />
              </UitkCardTextSegment>
            )}
          </div>
        </UitkCardContent>
      </UitkCard>
    </div>
  );
};

export default Links;
